import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";

import App from "./page/App";
import Homepage from "./page/Homepage";
import ReportTool from "./page/ReportTool.tsx";
import AuthCallback from "./page/AuthCallback";
import ProtectedRoute from "./page/ProtectedRoute";
import { API_BASE } from "./services/apiBase.ts";
import {
  ADMIN_PREVIEW_CONTEXT_EVENT,
  getAdminPreviewContextKey,
} from "./services/saved.ts";

import { AuthProvider, useAuth } from "react-oidc-context";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useNavigate } from "react-router-dom";

import reportWebVitals from "./reportWebVitals";
import {
  MetabaseProvider,
  defineMetabaseAuthConfig,
  defineMetabaseTheme,
} from "@metabase/embedding-sdk-react";

// ---------- Cognito config ----------
const cognitoAuthConfig = {
  authority: process.env.REACT_APP_COGNITO_AUTHORITY,
  client_id: process.env.REACT_APP_COGNITO_CLIENT_ID,
  redirect_uri: process.env.REACT_APP_COGNITO_REDIRECT_URI,
  post_logout_redirect_uri: process.env.REACT_APP_COGNITO_LOGOUT_REDIRECT_URI,
  response_type: "code",
  scope: "openid email",
  automaticSilentRenew: true,
};

const METABASE_INSTANCE_URL =
  process.env.REACT_APP_METABASE_INSTANCE_URL ||
  window.location.origin;

if (process.env.NODE_ENV === "development") {
  const originalConsoleError = console.error;
  console.error = (...args) => {
    const message = String(args[0] || "");
    const isMetabaseCallbackRefWarning =
      message.includes("Unexpected return value from a callback ref") &&
      message.includes("A callback ref should not return a function");

    if (isMetabaseCallbackRefWarning) {
      return;
    }

    originalConsoleError(...args);
  };
}

// ---------- Metabase SDK theme ----------
const arivaDarkTheme = defineMetabaseTheme({
  fontFamily: "Roboto, Inter, system-ui, Segoe UI, Helvetica, Arial, sans-serif",
  fontSize: "14px",
  colors: {
    background: "#071722",                 // --bg-main
    "background-secondary": "#071722",     // --bg-panel
    "background-hover": "#e04f2728",       // --hover
    border: "#243241",                     // --divider
    brand: "#e04e27",                      // --primary
    "brand-hover": "#e04f2728",              // --active-accent
    "text-primary": "#e6edf3",             // --text
    "text-secondary": "#b9c6d3",           // --text-dim
    "text-tertiary": "#b9c6d3",            // --text-dim
    "text-white": "#ffffff",
  },
  components: {
    dashboard: {
      backgroundColor: "#071722",
      card: {
        backgroundColor: "#071722",
        border: "1px solid #243241",
      },
      gridBorderColor: "#243241",
    },
    question: {
      backgroundColor: "#071722",
      toolbar: {
        backgroundColor: "#071722",
      },
    },
    table: {
      stickyBackgroundColor: "#071722",
      cell: {
        backgroundColor: "#071722",
        textColor: "#e6edf3",
        fontSize: "13px",
      },
    },
    tooltip: {
      backgroundColor: "#071722",
      focusedBackgroundColor: "#e04f2728",
      textColor: "#e6edf3",
      secondaryTextColor: "#b9c6d3",
    },
  },
});

const arivaLightTheme = defineMetabaseTheme({
  fontFamily: "Roboto, Inter, system-ui, Segoe UI, Helvetica, Arial, sans-serif",
  fontSize: "14px",
  colors: {
    background: "#ffffff",                 // --bg-main
    "background-secondary": "#ffffff",     // --bg-panel
    "background-hover": "#e04f2728",       // --hover
    border: "#e5e7eb",                     // --divider
    brand: "#e04e27",                      // --primary
    "brand-hover": "#e04f2728",              // --active-accent
    "text-primary": "#0f172a",             // --text
    "text-secondary": "#334155",           // --text-dim
    "text-tertiary": "#334155",            // --text-dim
    "text-white": "#ffffff",
  },
  components: {
    dashboard: {
      backgroundColor: "#ffffff",
      card: {
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
      },
      gridBorderColor: "#e5e7eb",
    },
    question: {
      backgroundColor: "#ffffff",
      toolbar: {
        backgroundColor: "#ffffff",
      },
    },
    table: {
      stickyBackgroundColor: "#ffffff",
      cell: {
        backgroundColor: "#ffffff",
        textColor: "#0f172a",
        fontSize: "13px",
      },
    },
    tooltip: {
      backgroundColor: "#ffffff",
      focusedBackgroundColor: "#e04f2728",
      textColor: "#0f172a",
      secondaryTextColor: "#334155",
    },
  },
});


const MetabaseErrorFallback = ({ message, error }) => {
  console.error("Metabase SDK error:", message, error);
  return (
    <div style={{ padding: 16, color: "#f88" }}>
      Something went wrong in the report builder.
      <br />
      Try reloading the page.
    </div>
  );
};

function HomeSelector() {
  const [params] = useSearchParams();
  const airport = (params.get("airport") || "").toLowerCase();
  if (["bos", "pbi", "lna", "syd"].includes(airport)) {
    return <Homepage sSystemName={airport} />;
  }
  return <App />;
}

function onSigninCallback() {
  window.history.replaceState({}, document.title, window.location.pathname);
}

function LogoutRoute() {
  const auth = useAuth();
  React.useEffect(() => {
    auth.signoutRedirect();
  }, [auth]);
  return <div>Signing out…</div>;
}

/**
 * ✅ This component is INSIDE BrowserRouter, so useNavigate works.
 * ✅ Put pluginsConfig on MetabaseProvider.
 */
function ProvidersAndRoutes() {
  const navigate = useNavigate();
  const lastClickLabelRef = React.useRef(null);

  const [themeMode, setThemeMode] = React.useState(
    () => localStorage.getItem("rtTheme") || "light"
  );

  React.useEffect(() => {
    localStorage.setItem("rtTheme", themeMode);
    document.documentElement.setAttribute("data-mantine-color-scheme", themeMode);
  }, [themeMode]);

  const pluginsConfig = React.useMemo(() => ({
    handleLink: (urlString) => {
      const mbName = lastClickLabelRef.current;
      const qs =
        `mb=${encodeURIComponent(urlString)}` +
        (mbName ? `&mbName=${encodeURIComponent(mbName)}` : "");
      navigate(`/reporttool?${qs}`);
      lastClickLabelRef.current = null;
      return { handled: true };
    },
    mapQuestionClickActions: (clickActions) =>
      clickActions.map((a) => {
        if (typeof a?.onClick !== "function") return a;
        const label = a.title || a.name || a.label || a.text;
        return {
          ...a,
          onClick: (ctx) => {
            lastClickLabelRef.current = label || null;
            a.onClick(ctx);
          },
        };
      }),
  }), [navigate]);

  return (
    <AuthProvider {...cognitoAuthConfig} onSigninCallback={onSigninCallback}>
      <Routes>
        <Route path="/" element={<HomeSelector />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/logout" element={<LogoutRoute />} />

        <Route
          path="/reporttool"
          element={
            <ProtectedRoute>
              <ReportTool
                themeMode={themeMode}
                setThemeMode={setThemeMode}
                renderWithMetabase={(purpose, content) => (
                  <MetabaseBridge
                    purpose={purpose}
                    themeMode={themeMode}
                    pluginsConfig={pluginsConfig}
                  >
                    {content}
                  </MetabaseBridge>
                )}
              />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}


function MetabaseBridge({ purpose = "runtime", themeMode, pluginsConfig, children }) {
  const auth = useAuth();

  const accessToken = auth.user?.access_token;
  const email = auth.user?.profile?.email;
  const [previewContextKey, setPreviewContextKey] = React.useState(
    () => getAdminPreviewContextKey()
  );
  const [previewTenantId, previewRoleId] = previewContextKey.split(":");
  const previewHeaders = React.useMemo(
    () => previewTenantId !== "none"
      ? {
          "X-Reporttool-Admin-Tenant": previewTenantId,
          ...(previewRoleId !== "none"
            ? { "X-Reporttool-Admin-Role": previewRoleId }
            : {}),
        }
      : {},
    [previewTenantId, previewRoleId]
  );

  React.useEffect(() => {
    const handlePreviewContextChange = () => {
      setPreviewContextKey(getAdminPreviewContextKey());
    };
    window.addEventListener(ADMIN_PREVIEW_CONTEXT_EVENT, handlePreviewContextChange);
    return () => window.removeEventListener(
      ADMIN_PREVIEW_CONTEXT_EVENT,
      handlePreviewContextChange
    );
  }, []);

  const metabaseAuthConfig = React.useMemo(() => (
    defineMetabaseAuthConfig({
      metabaseInstanceUrl: METABASE_INSTANCE_URL,
      preferredAuthMethod: "jwt",
      fetchRequestToken: async () => {
        if (!accessToken) throw new Error("No access token yet");

        const resp = await fetch(
          `${API_BASE}/api/auth/metabase-jwt?response=json&purpose=${encodeURIComponent(purpose)}`,
          {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            ...(email ? { "X-User-Email": email } : {}),
            ...previewHeaders,
          },
          cache: "no-store",
          },
        );

        const contentType = resp.headers.get("content-type") || "";
        const bodyText = await resp.text();

        if (!resp.ok) {
          console.error("JWT endpoint failed:", resp.status, contentType, bodyText.slice(0, 300));
          throw new Error(`JWT endpoint failed: ${resp.status}`);
        }

        if (!contentType.includes("application/json")) {
          console.error("Expected JSON, got:", contentType, bodyText.slice(0, 300));
          throw new Error("JWT endpoint did not return JSON");
        }

        return JSON.parse(bodyText);
      }
    })
  ), [accessToken, email, purpose, previewHeaders]);

  // Optional: avoid initializing SDK until auth finishes
  if (auth.isLoading) return <div>Loading…</div>;
  if (!auth.isAuthenticated) return <div>Redirecting…</div>;

  return (
    <MetabaseProvider
      key={`mb-${purpose}-${email ?? "no-email"}-${themeMode}-${accessToken?.slice(-12) ?? "no-token"}-${previewContextKey}`}
      authConfig={metabaseAuthConfig}
      theme={themeMode === "dark" ? arivaDarkTheme : arivaLightTheme}
      pluginsConfig={pluginsConfig}
      allowConsoleLog={true}
      errorComponent={MetabaseErrorFallback}
    >
      {children}
    </MetabaseProvider>
  );
}


const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <BrowserRouter>
    <ProvidersAndRoutes />
  </BrowserRouter>
);

reportWebVitals();
